import { useState } from 'react'
import { GridRowParams } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { formatShortDateWithTime } from '@dhl/utils'
import { Body1, Box, Button, Card, IconButton } from '@dhl-ui/components'

import { type GridColDef, Pagination } from '@dhl/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { UnlinkIcon } from '@dhl-ui/icons'
import {
  useDeleteOrganisationAccountManager,
  useGetAccountManagersByOrganisation,
  useGetUsersHook,
  useAddOrganisationAccountManagersHook,
  UserType,
  type UserDto,
} from '@dhl/mdp-api'

import {
  AdminDataGrid,
  useNotificationMessage,
  SelectableDataTableDialog,
  type GetDataFn,
  type UpdateDataFn,
} from '~/common'
import type { Organisation } from '~/modules/organisations/model'

type Props = {
  organisation: Organisation
}

export const OrganisationsAccountManagersCard = ({ organisation: { id: organisationId } }: Props) => {
  const [page, setPage] = useState<number>(0)
  const [perPage, setPerPage] = useState<number>(10)
  const [accountManagerDialogOpen, setAccountManagerDialogOpen] = useState<boolean>(false)
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<UserDto | undefined>(undefined)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showError, showSuccess } = useNotificationMessage()
  const { data, refetch: refetchAccountManagers } = useGetAccountManagersByOrganisation(organisationId, {
    skip: page * perPage,
  })
  const { mutateAsync: deleteAccountManager } = useDeleteOrganisationAccountManager()
  const getUsers = useGetUsersHook()
  const addOrganisationAccountManagersHook = useAddOrganisationAccountManagersHook()

  const unlinkAccountManager = () => {
    if (selectedUser) {
      deleteAccountManager({
        id: organisationId,
        userId: selectedUser.id,
      })
        .then(() => {
          showSuccess(t('admin.accountManagers.accountManagerUnlinked'))
          refetchAccountManagers().then(() => {})
        })
        .catch(() => showError(t('admin.accountManagers.accountManagerUnlinkFailed')))
        .finally(() => {
          setShowUnlinkConfirm(false)
        })
    }
  }

  const columns: GridColDef<UserDto>[] = [
    {
      field: 'email.address',
      headerName: t('admin.accountManagers.email'),
      sortable: true,
      width: 120,
      renderCell: ({ row: user }) => <Body1>{user?.email?.address ?? ''}</Body1>,
    },
    {
      field: 'name',
      headerName: t('admin.accountManagers.name'),
      sortable: true,
      width: 120,
      renderCell: ({ row: user }) => (
        <Body1>
          {user?.name?.firstName ?? ''} {user?.name?.lastName ?? ''}
        </Body1>
      ),
    },
    {
      field: 'timeLastLogin',
      headerName: t('admin.accountManagers.timeLastLogin'),
      sortable: true,
      flex: 1,
      renderCell: ({ row: user }) => <Body1>{user ? formatShortDateWithTime(user.timeLastLogin) : '-'}</Body1>,
    },
    {
      field: 'timeRegistered',
      headerName: t('admin.accountManagers.timeRegistered'),
      sortable: true,
      flex: 1,
      renderCell: ({ row: user }) => <Body1>{user ? formatShortDateWithTime(user.timeRegistered) : '-'}</Body1>,
    },
    {
      field: 'actions',
      headerName: t('admin.accountManagers.actions'),
      width: 60,
      renderCell: ({ row: user }) => (
        <IconButton
          aria-label="unlink user"
          onClick={() => {
            setSelectedUser(user)
            setShowUnlinkConfirm(true)
          }}
        >
          <UnlinkIcon />
        </IconButton>
      ),
    },
  ]

  const fetchData: GetDataFn<UserDto> = async (page = 0, perPage = 0, query = '') => {
    const { totalCount, content = [] } = await getUsers({
      userType: [UserType.accountManager],
      query,
      skip: page * perPage,
      max: perPage,
    })
    return [totalCount, content]
  }
  const handleUpdate: UpdateDataFn = (accountManagers: string[]) => {
    addOrganisationAccountManagersHook(organisationId, { accountManagers })
      .then(() => {
        showSuccess(t('admin.usersPage.accountManagers.accountManagerLinked'))
        refetchAccountManagers().then(() => {})
      })
      .catch(() => showError(t('admin.usersPage.accountManagers.accountManagerLinkFailed')))
  }

  const handleRowClick = ({ id }: GridRowParams<UserDto>) => {
    navigate(`/users/details/${id}`)
  }

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.accountManagers.title.organisations')}
          endNode={
            <Button variant="contained" onClick={() => setAccountManagerDialogOpen(true)}>
              {t('admin.accountManagers.addAccountManager.organisations')}
            </Button>
          }
        />
        <Card.Content>
          <AdminDataGrid columns={columns} rows={data?.content ?? []} hideFooter onRowClick={handleRowClick} />
          <Box px={2} py={1}>
            <Pagination
              page={page}
              count={data?.totalCount || 0}
              rowsPerPage={perPage}
              onPageChange={(_, newPage) => {
                setPage(newPage)
              }}
              onRowsPerPageChange={setPerPage}
            />
          </Box>
        </Card.Content>
      </Card>
      {showUnlinkConfirm && (
        <ConfirmationDialog
          open={true}
          titleText={t('admin.accountManagers.dangerZone')}
          acceptText={t('admin.accountManagers.unlinkAccountManager')}
          rejectText={t('admin.accountManagers.cancel')}
          onAccept={unlinkAccountManager}
          onReject={() => setShowUnlinkConfirm(false)}
          onClose={() => setShowUnlinkConfirm(false)}
        />
      )}
      {accountManagerDialogOpen && (
        <SelectableDataTableDialog<UserDto>
          open={accountManagerDialogOpen}
          title={t('admin.accountManagers.addAccountManager.organisations')}
          columns={[
            {
              field: 'name',
              headerName: t('admin.accountManagers.name'),
              width: 120,
              renderCell: ({ row }) => (
                <Body1>{[row?.name?.firstName ?? '', row?.name?.lastName ?? ''].join(' ')}</Body1>
              ),
            },
            {
              field: 'email.address',
              headerName: t('admin.accountManagers.email'),
              flex: 1,
              renderCell: ({ row }) => <Body1>{row?.email?.address ?? ''}</Body1>,
            },
          ]}
          getData={fetchData}
          onUpdate={handleUpdate}
          onClose={() => setAccountManagerDialogOpen(false)}
          disabledRowIds={data?.content?.map(({ id }) => id) ?? []}
        />
      )}
    </>
  )
}
